import { selectUser } from '@centerpiece/components';
import { createContext, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useGetMessageContactsQuery,
  useGetCommentContactsQuery,
  useGetContactQuery,
  useGetContactsQuery,
  useGetRolesQuery,
} from 'store/api';
import {
  CommentContact,
  Contact,
  MessageContact,
  Role,
} from 'store/api/models';

export type IndexContextContent = {
  contactRecord: Contact;
  msgs: MessageContact[];
  cmts: CommentContact[];
  refetchCount: number;
  refetchIndex: Function;
  contacts: { [key: number]: Contact };
  roles: { [key: number]: Role };
};
export const IndexContext = createContext<IndexContextContent>({
  contactRecord: {} as Contact,
  msgs: [],
  cmts: [],
  refetchCount: 0,
  refetchIndex: () => {},
  contacts: {},
  roles: {},
});

export const IndexProvider = ({ children }: { children: any }) => {
  const [refetchCount, setRefetchCount] = useState<number>(0);

  const refetchIndex = () => setRefetchCount(refetchCount => refetchCount + 1);
  const { contact: userContact } = useSelector(selectUser);

  const { data: contactRecord = {} as Contact, isLoading } = useGetContactQuery(
    { id: userContact, params: { nested: 'all' } },
    { skip: !userContact }
  );
  const { data: contactsRaw = [] } = useGetContactsQuery({});
  const contacts = useMemo(() => {
    let contacts: { [key: number]: Contact } = {};
    contactsRaw.forEach((c: Contact) => {
      contacts[c.id] = c;
    });
    return contacts;
  }, [contactsRaw]);
  const { data: rolesRaw = [] } = useGetRolesQuery({});
  const roles = useMemo(() => {
    let roles: { [key: number]: Role } = {};
    rolesRaw.forEach((r: Role) => {
      roles[r.id] = r;
    });
    return roles;
  }, [rolesRaw]);
  const { data: msgs = [] } = useGetMessageContactsQuery(
    {
      params: {
        contact: userContact,
        author: false,
        status: 'New',
        rollup: 'project',
      },
    },
    { skip: userContact <= 0 }
  );
  const { data: cmts = [] } = useGetCommentContactsQuery(
    {
      params: {
        contact: userContact,
        author: false,
        status: 'New',
        rollup: 'project',
      },
    },
    { skip: userContact <= 0 }
  );
  return (
    <IndexContext.Provider
      value={{
        contactRecord,
        msgs,
        cmts,
        refetchCount,
        refetchIndex,
        contacts,
        roles,
      }}
    >
      {children}
    </IndexContext.Provider>
  );
};

export const useIndexContext = () => useContext(IndexContext);
